import * as React from "react";

import Layout from "../../components/Layout";
import styled from "styled-components";
import { select } from "@hubper/theme";

import * as langApi from "../../api/lang";
import { getSettings, Settings } from "../../api/settings";

import { Spinner } from "@hubper/core-ui/elements";
import { useNotify, useModal } from "@hubper/core-ui/hooks";

import EditGrid from "./components/EditGrid";
import Options from "./components/Options";

import produce from "immer";
import { TabBar } from "@hubper/core-ui/components";
import Confirm from "@hubper/core-ui/modals/Confirm";

/**
 * Styles
 */

const Main = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
`;

const Right = styled.div`
  width: 400px;
  position: absolute;
  right: 0;
  top: 1px;
  bottom: 0;
  margin-left: 24px;
  padding: 24px 32px;
  border-left: 1px solid ${select.color(["grey", 0])};
  background-color: white;
  z-index: 10;
`;

/**
 * Main
 */

type State = {
  loading: boolean;
  availableLanguages: string[];
  translations: Record<string, object>;
  settings: Settings | null;
  selectedLanguages: string[];
  searchString: string;
  showOnlyEmpty: boolean;
  showNs: string;
};

function Home({
  context,
  onContextChange,
}: {
  context: langApi.Context;
  onContextChange: (context: langApi.Context) => void;
}) {
  const [state, setState] = React.useState<State>({
    loading: true,
    availableLanguages: [],
    translations: {},
    settings: null,
    selectedLanguages: [],
    searchString: "",
    showOnlyEmpty: false,
    showNs: "ALL",
  });

  const notify = useNotify();
  const { showModal } = useModal();

  React.useEffect(() => {
    getSettings().then((settings) => {
      Promise.all([
        langApi.getAvailableLanguages(),
        langApi.getLanguage(context, settings.referenceLanguage),
      ] as const).then(([availableLanguages, initialTranslation]) => {
        setState({
          ...state,
          loading: false,
          availableLanguages,
          translations: {
            [settings.referenceLanguage]: initialTranslation,
          },
          settings,
          selectedLanguages: [settings.referenceLanguage],
        });
      });
    });
  }, []);

  return (
    <Layout
      bodyStyle={{
        position: "relative",
        display: "flex",
        paddingTop: 0,
      }}
    >
      <Main>
        <TabBar
          // style={{ marginTop: 100 }}
          items={[
            { value: "web", display: "Web" },
            { value: "app", display: "App" },
            { value: "api", display: "Api" },
          ]}
          selectedItem={context}
          onChange={onContextChange as any}
        />
        <h1 style={{ margin: 0, marginTop: 16 }}>Edit</h1>

        {state.loading ? (
          <Spinner />
        ) : (
          <>
            <EditGrid
              referenceLanguage={state.settings!.referenceLanguage}
              translations={state.selectedLanguages.reduce((red, lang) => {
                red[lang] = state.translations[lang];
                return red;
              }, {} as any)}
              searchString={state.searchString.toLowerCase()}
              showOnlyEmpty={state.showOnlyEmpty}
              showNs={state.showNs}
              onEntryChange={(mutation) => {
                setState((s) => ({
                  ...s,
                  translations: produce(s.translations, (translations: any) => {
                    translations[mutation.language!][mutation.ns][
                      mutation.key
                    ] = mutation.value;
                  }),
                }));
              }}
              onEntryBlur={(payload) =>
                langApi
                  .mutateEntry(context, payload)
                  .then(() => {
                    notify({
                      title: "Entry saved",
                      description: `${payload.language!.toUpperCase()}:${
                        payload.ns
                      }:${payload.key}`,
                      type: "success",
                    });
                  })
                  .catch(() =>
                    notify({
                      title: "Could not save entry",
                      description: `${payload.language!.toUpperCase()}:${
                        payload.ns
                      }:${payload.key}`,
                      type: "error",
                    }),
                  )
              }
              onEntryDelete={(ns, key) => {
                showModal(
                  <Confirm
                    okLabel="Delete"
                    cancelLabel="Cancel"
                    type="delete"
                    message={`Are you shure you want to remove this entry: ${key}?`}
                    title="Delete entry"
                    onConfirm={() => {
                      langApi
                        .deleteEntry(context, ns, key)
                        .then(() => {
                          notify({
                            title: "Entry deleted",
                            description: `${ns}:${key}`,
                            type: "success",
                          });
                        })
                        .catch(() => {
                          notify({
                            title: "Could not delete entry",
                            description: `${ns}:${key}`,
                            type: "error",
                          });
                        });

                      setState((s) => ({
                        ...s,
                        translations: produce(
                          s.translations,
                          (translations: any) => {
                            Object.keys(s.translations).forEach((lang) => {
                              delete translations[lang][ns][key];
                            });
                          },
                        ),
                      }));
                    }}
                  />,
                );
              }}
              onNsDelete={(ns) => {
                showModal(
                  <Confirm
                    okLabel="Delete"
                    cancelLabel="Cancel"
                    type="delete"
                    message={`Are you shure you want to remove this namespace: ${ns}? Warning: all entries will be gone!!`}
                    title="Delete namespace"
                    onConfirm={() => {
                      langApi
                        .deleteNamespace(context, ns)
                        .then(() => {
                          notify({
                            title: "Namespace deleted",
                            description: `${ns}`,
                            type: "success",
                          });
                        })
                        .catch(() => {
                          notify({
                            title: "Could not delete namespace",
                            description: `${ns}`,
                            type: "error",
                          });
                        });

                      setState((s) => ({
                        ...s,
                        translations: produce(
                          s.translations,
                          (translations: any) => {
                            Object.keys(s.translations).forEach((lang) => {
                              delete translations[lang][ns];
                            });
                          },
                        ),
                      }));
                    }}
                  />,
                );
              }}
              onEntryAdd={(ns, key) => {
                langApi
                  .createEntry(context, {
                    ns,
                    key,
                    value: null,
                  })
                  .then(() => {
                    notify({
                      title: "Entry added",
                      description: `${ns}:${key}`,
                      type: "success",
                    });
                  })
                  .catch(() => {
                    notify({
                      title: "Could not add namespace",
                      description: `${ns}:${key}`,
                      type: "error",
                    });
                  });

                setState((s) => ({
                  ...s,
                  translations: produce(s.translations, (translations: any) => {
                    Object.keys(s.translations).forEach((lang) => {
                      translations[lang][ns][key] = null;
                    });
                  }),
                }));
              }}
              onNsAdd={(newNs, afterNs) => {
                langApi
                  .createNamespace(context, newNs)
                  .then(() => {
                    notify({
                      title: "Namespace added",
                      description: `${newNs}`,
                      type: "success",
                    });
                  })
                  .catch(() => {
                    notify({
                      title: "Could not add namespace",
                      description: `${newNs}`,
                      type: "error",
                    });
                  });

                setState((s) => ({
                  ...s,
                  translations: produce(s.translations, (translations: any) => {
                    Object.keys(s.translations).forEach((lang) => {
                      const n: any = {};

                      for (const [ns, content] of Object.entries(
                        translations[lang],
                      )) {
                        n[ns] = content;
                        if (ns === afterNs) {
                          n[newNs] = {};
                        }
                      }

                      translations[lang] = n;
                    });
                  }),
                }));
              }}
              onKeyChange={(ns, oldKey, newKey) => {
                langApi
                  .renameKey(context, ns, oldKey, newKey)
                  .then(() => {
                    notify({
                      title: "Renamed key",
                      description: `${oldKey} -> ${newKey}`,
                      type: "success",
                    });
                  })
                  .catch(() => {
                    notify({
                      title: "Could not rename key",
                      description: `${oldKey} -> ${newKey}`,
                      type: "error",
                    });
                  });

                setState((s) => ({
                  ...s,
                  translations: produce(s.translations, (translations: any) => {
                    Object.keys(s.translations).forEach((lang) => {
                      const n: any = {};
                      // @ts-ignore
                      Object.keys(s.translations[lang][ns]).forEach((key) => {
                        if (key === oldKey) {
                          // @ts-ignore
                          n[newKey] = s.translations[lang][ns][key];
                        } else {
                          // @ts-ignore
                          n[key] = s.translations[lang][ns][key];
                        }
                      });
                      translations[lang][ns] = n;
                    });
                  }),
                }));
              }}
              onNsChange={(oldNs, newNs) => {
                langApi
                  .renameNamespace(context, oldNs, newNs)
                  .then(() => {
                    notify({
                      title: "Renamed namespace",
                      description: `${oldNs} -> ${newNs}`,
                      type: "success",
                    });
                  })
                  .catch(() => {
                    notify({
                      title: "Could not rename namespace",
                      description: `${oldNs} -> ${newNs}`,
                      type: "error",
                    });
                  });

                setState((s) => ({
                  ...s,
                  translations: produce(s.translations, (translations: any) => {
                    Object.keys(s.translations).forEach((lang) => {
                      const n: any = {};
                      Object.keys(s.translations[lang]).forEach((ns) => {
                        if (ns === oldNs) {
                          // @ts-ignore
                          n[newNs] = s.translations[lang][ns];
                        } else {
                          // @ts-ignore
                          n[ns] = s.translations[lang][ns];
                        }
                      });
                      translations[lang] = n;
                    });
                  }),
                }));
              }}
            />
          </>
        )}
      </Main>
      <Right>
        {state.loading ? (
          <Spinner />
        ) : (
          <Options
            availableLanguages={state.availableLanguages}
            selectedLanguages={state.selectedLanguages}
            referenceLanguage={state.settings!.referenceLanguage}
            onSelectedLanguagesChange={async (language, type) => {
              let newSelection =
                type === "add"
                  ? state.selectedLanguages.concat(language)
                  : state.selectedLanguages.filter((x) => x !== language);

              let newTranslations = { ...state.translations };
              if (type === "add") {
                const translation = await langApi.getLanguage(
                  context,
                  language,
                );
                newTranslations[language] = translation;
              } else {
                delete newTranslations[language];
              }

              setState((s) => ({
                ...s,
                selectedLanguages: newSelection,
                translations: newTranslations,
              }));
            }}
            searchString={state.searchString}
            onSearchStringChange={(searchString) =>
              setState({ ...state, searchString })
            }
            showOnlyEmpty={state.showOnlyEmpty}
            onShowOnlyEmptyChange={(showOnlyEmpty) =>
              setState((s) => ({ ...s, showOnlyEmpty }))
            }
            showNs={state.showNs}
            availableNamespaces={Object.keys(
              state.translations[state.settings!.referenceLanguage],
            )}
            onShowNsChange={(showNs) => setState((s) => ({ ...s, showNs }))}
          />
        )}
      </Right>
    </Layout>
  );
}

export default function ContextWrapper() {
  const [context, setContext] = React.useState<langApi.Context>("web");

  return <Home key={context} context={context} onContextChange={setContext} />;
}
